import { appById } from '../../os/apps.js';
import { activeId, closeApp, closeWindow, enterFullscreen, exitWindowMode, focusWindow, getWindow, launchApp, minimizeApp, minimizeWindow, cascadeWindows, tileWindows, enterSplitLeft, enterSplitRight, moveWindowToSpace } from '../../os/windows.svelte.js';
import { spacesState, switchSpace, toggleMissionControl } from '../../os/spaces.svelte.js';
import { emptyTrash, trashItems } from '../../os/filesystem.svelte.js';
import { openFile } from '../../os/documents.svelte.js';
import { clearRecents } from '../../os/recents.svelte.js';
import { logoutAdmin, shutdownOS, restartOS } from '../../os/session.svelte.js';

export function runMenuItem(item, activeApp) {
  if (item.disabled || item.separator || item.heading) return;
  if (item.clearRecents) { clearRecents(); return; }
  if (item.recent?.kind === 'app') { launchApp(appById(item.recent.id)); return; }
  if (item.recent?.kind === 'file') { openFile(item.recent.id); return; }
  if (item.app) launchApp(appById(item.app));
  if (!item.command) return;
  switch (item.command.type) {
    case 'minimize': { const id = activeId(); if (id) minimizeWindow(id); break; }
    case 'exit-window-mode': { const id = activeId(); if (id) exitWindowMode(id); break; }
    case 'cascade': cascadeWindows(); break;
    case 'tile-all': tileWindows(); break;
    case 'tile-left': { const id = activeId(); if (id) enterSplitLeft(id); break; }
    case 'tile-right': { const id = activeId(); if (id) enterSplitRight(id); break; }
    case 'mission-control': toggleMissionControl(); break;
    case 'previous-space':
    case 'next-space': {
      const index = spacesState.spaces.findIndex((space) => space.id === spacesState.activeSpaceId);
      const delta = item.command.type === 'previous-space' ? -1 : 1;
      const target = spacesState.spaces[index + delta];
      if (target) switchSpace(target.id);
      break;
    }
    case 'empty-trash': { if (trashItems().length && confirm('Permanently delete every item in Trash?')) emptyTrash(); break; }
    case 'new-window': launchApp(activeApp); break;
    case 'close-window': { const id = activeId(); if (id) closeWindow(id); break; }
    case 'hide-app': minimizeApp(activeApp.id); break;
    case 'quit-app': closeApp(activeApp.id); break;
    case 'focus-window': focusWindow(item.command.id); break;
    case 'move-to-space': { const id = activeId(); if (id) moveWindowToSpace(id, item.command.spaceId); break; }
    case 'toggle-fullscreen': { const id = activeId(); if (id) enterFullscreen(id); break; }
    case 'app-command':
      globalThis.dispatchEvent(new CustomEvent('app-menu-command', { detail: { appId: activeApp.id, windowId: activeId(), action: item.command.action } }));
      break;
    case 'enter-sandbox':
      enterSandboxMode();
      break;
    case 'logout':
      if (confirm('Are you sure you want to log out?')) logoutAdmin();
      break;
    case 'sleep':
      globalThis.dispatchEvent(new CustomEvent('os-power', { detail: { action: 'sleep' } }));
      break;
    case 'restart':
      if (confirm('Are you sure you want to restart? Unsaved changes will be lost.')) restartOS();
      break;
    case 'shutdown':
      if (confirm('Are you sure you want to shut down? Unsaved changes will be lost.')) shutdownOS();
      break;
    default:
      launchApp(appById('projects'));
      requestAnimationFrame(() => { globalThis.dispatchEvent(new CustomEvent('finder-command', { detail: item.command })); });
  }
}

async function enterSandboxMode() {
  try {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
      if (navigator.keyboard && navigator.keyboard.lock) await navigator.keyboard.lock();
    } else {
      if (document.exitFullscreen) await document.exitFullscreen();
    }
  } catch (err) {
    console.error("Error attempting to toggle sandbox/fullscreen:", err);
    alert("Fullscreen/Keyboard Lock API not fully supported or blocked by browser.");
  }
}

export function navigateMenu(e, state) {
  const { activeMenu, menuDefs, activeItemIndex } = state;
  if (!activeMenu) {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      return { activeMenu: menuDefs[0]?.id ?? null, activeItemIndex: 0 };
    }
    return null;
  }
  const currentMenu = menuDefs.find(m => m.id === activeMenu);
  if (!currentMenu) return null;
  const items = currentMenu.items.filter(item => !item.separator && !item.heading);
  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      return { activeMenu, activeItemIndex: Math.min(activeItemIndex + 1, items.length - 1) };
    case 'ArrowUp':
      e.preventDefault();
      return { activeMenu, activeItemIndex: Math.max(activeItemIndex - 1, 0) };
    case 'ArrowLeft': {
      e.preventDefault();
      const menuIds = menuDefs.map(m => m.id);
      const idx = menuIds.indexOf(activeMenu);
      return { activeMenu: menuIds[Math.max(0, idx - 1)], activeItemIndex: 0 };
    }
    case 'ArrowRight': {
      e.preventDefault();
      const menuIds = menuDefs.map(m => m.id);
      const idx = menuIds.indexOf(activeMenu);
      return { activeMenu: menuIds[Math.min(menuIds.length - 1, idx + 1)], activeItemIndex: 0 };
    }
    case 'Enter':
    case ' ':
      e.preventDefault();
      return { runItem: items[activeItemIndex] ?? null, activeMenu: null, activeItemIndex: 0 };
    case 'Escape':
      e.preventDefault();
      return { activeMenu: null, activeItemIndex: 0 };
  }
  return null;
}
